import * as styles from "./HeaderDesktop.module.scss";

import { navigate } from "@reach/router";
import React, { useEffect, useState } from "react";
import { useRef } from "react";
import { useCallback } from "react";
import { set } from "react-hook-form";
import { DataLayerWrapper } from "../../../../components/DataLayerWrapper/DataLayerWrapper";
import MyAccountIcon from "../../../../components/MyAccountIcon/MyAccountIcon";
import MyBasketIcon from "../../../../components/MyBasketIcon/MyBasketIcon";
import MyFavouritesIcon from "../../../../components/MyFavouritesIcon/MyFavouritesIcon";
import ToolBar from "../../../../components/ToolBar/ToolBar";
import { Button, TextInput } from "../../../../components/common";
import {
	BurgerMenu,
	PrimaryLogo,
	PrimaryLogoReduced,
	Search,
} from "../../../../components/common/Icons";
import LinkNoStyle from "../../../../components/common/LinkNoStyle/LinkNoStyle";
import { useMyAccountUK } from "../../../../context";
import { Colours } from "../../../../utils/colours";
import { isSpecificSite } from "../../../../utils/isSpecificSite";
import { getLogoSize } from "../Header.helpers";
import SkipToContent from "../SkipToContent/SkipToContent";
import { GOOGLE_SEARCH_BOX_ID, GOOGLE_SEARCH_BUTTON_CLASS } from "../consts";
import SecondaryNavigation, {
	type MenuOption,
} from "./Secondary/SecondaryNavigation";

const SCROLL_BREAK_POINT = 110;

export interface HeaderProps {
	nestedLinks: DogsTrust.MenuItem[];
	headerHeight: number;
	scrollHeight: number;
	breadcrumbs: DogsTrust.Breadcrumb[];
	promoCards: DogsTrust.PromotionCardToDisplayProps[];
}

export const HeaderDesktop: React.FC<HeaderProps> = ({
	nestedLinks,
	scrollHeight,
	headerHeight,
	breadcrumbs,
	promoCards,
}) => {
	isSpecificSite("uk", "HeaderDesktop");

	const [searchTerm, setSearchTerm] = useState("");
	const [iconSize, setIconSize] = useState(120);
	const [secondMenuToShow, setSecondMenuToShow] = useState<MenuOption>({
		title: "",
		id: "",
	});
	const { profile } = useMyAccountUK();
	const [prevScrollHeight, setPrevScrollHeight] = useState(scrollHeight);

	const handleSearch = useCallback(() => {
		const searchBox = document.getElementById(
			GOOGLE_SEARCH_BOX_ID,
		) as HTMLInputElement;
		const searchButton = document.getElementsByClassName(
			GOOGLE_SEARCH_BUTTON_CLASS,
		)[0] as HTMLButtonElement;

		if (searchBox && searchButton) {
			searchBox.value = searchTerm;
			searchBox.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter" }));
			searchButton.click();
		} else navigate(`/search?q=${searchTerm}`);
	}, [searchTerm]);

	const submitHandler: React.FormEventHandler<HTMLFormElement> = (e) => {
		e.preventDefault();
		handleSearch();
	};

	useEffect(() => {
		setIconSize(getLogoSize(iconSize, prevScrollHeight, scrollHeight));
		setPrevScrollHeight(scrollHeight);
	}, [scrollHeight, prevScrollHeight, iconSize]);

	const getContainerTop = () => {
		if (scrollHeight > SCROLL_BREAK_POINT) {
			return -106;
		}
	};
	const logoSize = iconSize > 105 ? "standard" : "small";

	const onClearNav = () => {
		setSecondMenuToShow(null);
		document.body.style.overflow = "auto";
	};

	const divRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (event: { target: unknown }) => {
			if (divRef.current && !divRef.current.contains(event.target)) {
				setSecondMenuToShow(null);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		/**
		 * Any time the page loads it should hide the nav which then frees the scroll up.
		 * As Gatsby works a bit like an SPA, it can hold the nav open and there is a race condition for unlocking the scroll, this makes it more imperitive.
		 */
		setSecondMenuToShow(null);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// When the nav is open, page should not be scrollable
	useEffect(() => {
		if (secondMenuToShow !== null) {
			document.body.style.overflow = "auto";
		} else {
			document.body.style.overflow = "auto";
		}

		return () => {
			document.body.style.overflow = "auto";
		};
	}, [secondMenuToShow]);

	return (
		<>
			<SkipToContent />

			<header
				id="dt-header"
				className={styles.headerouter}
				style={{
					top: getContainerTop(),
				}}
			>
				<div className={styles.headerinner}>
					<div
						className={styles.content}
						style={{
							justifyContent:
								scrollHeight < SCROLL_BREAK_POINT
									? "space-between"
									: "flex-end",
						}}
					>
						<a
							id={"logo-wrapper"}
							aria-label="Home"
							className={styles.logowrappertop}
							href={
								process.env.GATSBY_IS_PREVIEW === "true"
									? `${process.env.GATSBY_PUBLISHED_BASE_URL}`
									: "/"
							}
							style={{
								display: scrollHeight < SCROLL_BREAK_POINT ? "flex" : "none",
							}}
						>
							{logoSize === "standard" ? (
								<PrimaryLogo
									title={"Dog's Trust Home Page"}
									height={`${iconSize}px`}
									width={`${iconSize}px`}
								/>
							) : (
								<PrimaryLogoReduced
									title={"Dog's Trust Home Page"}
									height={`${iconSize}px`}
									width={`${iconSize}px`}
								/>
							)}
						</a>
						<div className={styles.contentrightwrapper}>
							<div className={styles.controlswrapper}>
								<div
									id="sitelookup"
									role="search"
									className={styles.searchboxwrapper}
								>
									<form onSubmit={submitHandler}>
										<TextInput
											name="search"
											placeholder={"Search"}
											className={styles.searchbox}
											IconRight={Search}
											onIconRightCallback={handleSearch}
											iconRightLabel="Search"
											onChange={(e) => setSearchTerm(e.currentTarget.value)}
											value={searchTerm}
										/>
									</form>
								</div>
								{process.env.GATSBY_FEATURE_FLAG_MY_ACCOUNT === "true" && (
									<div className={styles.accountwrapper}>
										<MyAccountIcon />
									</div>
								)}
								{profile.basket?.length > 0 && (
									<MyBasketIcon
										className={styles.basket}
										count={profile.basket?.length}
									/>
								)}
								{profile.favourites?.length > 0 && (
									<MyFavouritesIcon
										count={profile.favourites.length}
										className={styles.favs}
									/>
								)}

								<div className={styles.donatewrapper}>
									<Button
										variant="donate"
										fullWidth={true}
										url={"/support-us/donation"}
										ariaLabel=""
										size={"small"}
									>
										Donate
									</Button>
								</div>
								<div className={styles.burgerwrapper}>
									<BurgerMenu
										fill={Colours.MIDNIGHT}
										height={"32px"}
										width={"32px"}
									/>
								</div>
							</div>
						</div>
					</div>
					<nav
						className={styles.bottomlinkswrapper}
						style={{
							paddingLeft: 100,
							paddingRight: 65,
						}}
					>
						<a
							href={
								process.env.GATSBY_IS_PREVIEW === "true"
									? `${process.env.GATSBY_PUBLISHED_BASE_URL}`
									: "/"
							}
							className={styles.logowrapperbottom}
							aria-label="Dog's Trust Home Page"
							style={{
								display: scrollHeight > SCROLL_BREAK_POINT ? "flex" : "none",
							}}
						>
							<PrimaryLogoReduced height={"80px"} width={"80px"} />
						</a>
						<div ref={divRef} className={styles.primarylinks}>
							<ul className={styles.listoflinks}>
								{nestedLinks.map((link, i) => (
									<li key={`${link.title}-${i}`} className={styles.listlink}>
										<DataLayerWrapper
											event={"menu_navigation"}
											menu_level={"top"}
											menu_clicked={link.title}
											menu_url={link.url}
											onClick={onClearNav}
										>
											{link.secondLevelItems.length === 0 ? (
												<LinkNoStyle
													className={styles.primarylink}
													to={link.url}
													tabIndex={0}
													onKeyDown={onClearNav}
												>
													{link.title}
												</LinkNoStyle>
											) : (
												<Button
													variant="tertiary"
													className={styles.listlinkanchor}
													onClick={() =>
														secondMenuToShow?.id !== link.id
															? setSecondMenuToShow({
																	...link,
																})
															: setSecondMenuToShow(null)
													}
													ariaExpanded={link.id === secondMenuToShow?.id}
													ariaLabel={`${secondMenuToShow ? "Close" : "Open"} ${link.title} sub menu`}
													ariaHasPopup={"menu"}
												>
													{link.title}
												</Button>
											)}
										</DataLayerWrapper>
										{!!link.secondLevelItems.length && (
											<SecondaryNavigation
												key={link.id}
												isVisible={link.id === secondMenuToShow?.id}
												nestedLinks={link.secondLevelItems}
												onHide={onClearNav}
												promoCards={promoCards}
												primaryLink={link}
											/>
										)}
									</li>
								))}
							</ul>
						</div>
						{scrollHeight > 56 &&
							process.env.GATSBY_FEATURE_FLAG_MY_ACCOUNT === "true" && (
								<div className={styles.accountwrapper}>
									<MyAccountIcon />
								</div>
							)}
						{scrollHeight > 56 && profile.basket?.length > 0 && (
							<MyBasketIcon
								className={styles.basketscrolled}
								count={profile.basket?.length}
							/>
						)}
						{scrollHeight > 56 && profile.favourites.length > 0 && (
							<MyFavouritesIcon
								count={profile.favourites.length}
								className={styles.favsscrolled}
							/>
						)}
					</nav>
				</div>
				<ToolBar
					headerHeight={headerHeight}
					scrollHeight={scrollHeight}
					breadcrumbs={breadcrumbs}
				/>
			</header>
		</>
	);
};

export default HeaderDesktop;
